import FeatherIcon from "feather-icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Spinner,
  Collapse,
} from "reactstrap";
import api from "../helpers/api";
import { WEB_PORTAL_ALLOWED_ROLES, ROLES } from "../constants/permissions";
import PermissionsTable from "../components/PermissionsTable";
import UpgradeModal from "../components/UpgradeModal";
import { useTranslation } from 'react-i18next';

const Roles = () => {
  const { t } = useTranslation();

  const [roles, setRoles] = useState([]);
  const [groupedPermissions, setGroupedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRole, setExpandedRole] = useState(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  // Fetch roles
  const fetchRoles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("rbac/roles");
      setRoles(data.response || []);
    } catch (err) {
      console.error("Error fetching roles:", err);
      setError(t("business.roles.failed_load_roles", "Failed to load roles. Please try again."));
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch grouped permissions
  const fetchPermissions = useCallback(async () => {
    try {
      const { data } = await api.get("rbac/permissions/grouped");
      setGroupedPermissions(data.response || []);
    } catch (error) {
      console.error("Error fetching permissions:", error);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, [fetchRoles, fetchPermissions]);

  // Filter roles to show only allowed system roles + custom roles
  const displayableRoles = useMemo(() => {
    return roles.filter((role) => {
      if (role.systemRole) {
        return WEB_PORTAL_ALLOWED_ROLES.includes(role.name);
      }
      return true;
    });
  }, [roles]);

  // Map system roles to translatable display names and descriptions
  const roleMap = useMemo(() => ({
    [ROLES.SUPER_ADMIN]: {
      displayName: t('shared.role_super_admin', 'Super Admin'),
      description: t('business.roles.descriptions.super_admin', 'Complete access to all features and organization settings.')
    },
    [ROLES.ADMINISTRATOR]: {
      displayName: t('shared.role_administrator', 'Administrator'),
      description: t('business.roles.descriptions.administrator', 'Organization administrator with full org access')
    },
    [ROLES.FULL_USER]: {
      displayName: t('shared.role_full_user', 'Full User'),
      description: t('business.roles.descriptions.full_user', 'Technician with ownership-based access.')
    },
    [ROLES.REQUESTOR]: {
      displayName: t('shared.role_requester', 'Requestor'),
      description: t('business.roles.descriptions.requestor', 'Can scan assets and create/manage their own service requests.')
    },
    [ROLES.DEFAULT]: {
      displayName: t('shared.role_default', 'Default role'),
      description: t('business.roles.descriptions.default', 'Basic user with minimal access.')
    }
  }), [t]);

  // Toggle role expansion
  const toggleExpand = useCallback((roleId) => {
    setExpandedRole((prev) => (prev === roleId ? null : roleId));
  }, []);

  // Get role permissions as a Set for quick lookup
  const getRolePermissionSet = (role) => {
    if (!role.permissions) return new Set();
    return new Set(role.permissions.map((p) => p.name || p));
  };

  return (
    <>
      <div className="mb-4">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <button
            type="button"
            className="btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2 transition-all hover-shake"
            onClick={() => setUpgradeModalOpen(true)}
          >
            <FeatherIcon icon="lock" size={18} />
            {t("business.roles.new_role", "New Role")}
          </button>
        </motion.div>
      </div>

      <Card>
        <CardHeader>
          <h3 className="card-title text-success mb-0">{t("shared.roles_and_permissions", "Roles & Permissions")}</h3>
        </CardHeader>
        <CardBody>
          {error && (
            <div className="alert alert-danger d-flex justify-content-between align-items-center mb-3">
              <span>{error}</span>
              <button className="btn btn-sm btn-outline-danger" onClick={fetchRoles}>
                <FeatherIcon icon="refresh-cw" size={14} className="me-1" />
                {t("shared.retry", "Retry")}
              </button>
            </div>
          )}
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="mt-2 text-muted">{t("business.roles.loading_roles", "Loading roles...")}</p>
            </div>
          ) : displayableRoles.length === 0 ? (
            <div className="text-center py-5">
              <FeatherIcon icon="shield-off" size={48} className="text-muted mb-3" />
              <p className="text-muted">{t("business.roles.no_roles_found", "No roles found")}</p>
            </div>
          ) : (
            <AnimatePresence>
              {displayableRoles.map((role, index) => {
                const permissionSet = getRolePermissionSet(role);
                const isExpanded = expandedRole === role.id;

                return (
                  <motion.div
                    key={role.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <Card className="mb-3 shadow-sm border">
                      <CardHeader
                        className="d-flex justify-content-between align-items-center py-3"
                        style={{ cursor: "pointer" }}
                        onClick={() => toggleExpand(role.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleExpand(role.id);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isExpanded}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{
                              width: 40,
                              height: 40,
                              backgroundColor: "rgba(var(--bs-primary-rgb), 0.1)",
                            }}
                          >
                            <FeatherIcon
                              icon="shield"
                              size={20}
                              className="text-primary"
                            />
                          </div>
                          <div>
                            <h6 className="mb-0 fw-bold">
                              {roleMap[role.name]?.displayName || role.displayName || role.name}
                            </h6>
                            <small className="text-muted">
                              {roleMap[role.name]?.description || role.description || t("shared.no_description", "No description")}
                            </small>
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                          <span className="text-muted">
                            {role.userCount || 0} {role.userCount === 1 ? t("shared.user", "user") : t("shared.users", "users")}
                          </span>
                          <FeatherIcon
                            icon={isExpanded ? "chevron-up" : "chevron-down"}
                            size={18}
                            className="text-muted"
                          />
                        </div>
                      </CardHeader>

                      <Collapse isOpen={isExpanded}>
                        <CardBody className="border-top">
                          {groupedPermissions.length === 0 ? (
                            <div className="text-center py-3">
                              <Spinner size="sm" color="primary" />
                              <span className="ms-2">{t("shared.loading_permissions", "Loading permissions...")}</span>
                            </div>
                          ) : (
                            <PermissionsTable
                              groupedPermissions={groupedPermissions}
                              rolePermissions={permissionSet}
                              roleName={role.name}
                            />
                          )}
                        </CardBody>
                      </Collapse>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
        </CardBody>
      </Card>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={upgradeModalOpen}
        toggle={() => setUpgradeModalOpen(false)}
      />
    </>
  );
};

export default Roles;
